import { type PortalIcon, resolveIconSrc } from "@/api/apps";
import { cn } from "@/lib/utils";
import { resolveTheme, useThemeStore } from "@/stores/theme";

/**
 * The fixed-size, tinted tile a portal logo sits in (flowstack#1976,
 * flowstack#1978).
 *
 * Every card gets the SAME well: the `panel-well` token (one surface step
 * below the card, in both themes), rounded, with inner padding so no logo
 * runs to the edges. The two sizes are the design's:
 *
 * - `md` (Apps cards) — 20px by 24px of padding around a 60px-high box;
 * - `sm` (Dashboard Suite panel) — a 56px tile with the logo at most 40px.
 *
 * On the Apps card the image FILLS its 60px box under `object-contain` rather
 * than sitting at natural size under a `max-h` cap: a cap only ever shrinks,
 * so a PNG smaller than the box rendered smaller than its neighbours. The
 * Dashboard tile is a 40px cap in a box far smaller than any portal PNG, so
 * there the two are the same.
 *
 * That box is the PNG's canvas, transparent margin included, so no sizing
 * here can compensate for an empty band baked into the file — which is how
 * the light-theme Affinity-Rxiv wordmark read as the odd one out. The
 * margins are trimmed in the assets themselves, and
 * `portal-logos.test.ts` pins every `public/portals/` logo's artwork to
 * fill its canvas.
 *
 * Theme pairing is `resolveIconSrc` (`path_dark` on dark, else `path`), the
 * same helper the launcher always used, so the Dashboard and the Apps page
 * cannot pick different files for one portal.
 *
 * `muted` is the locked-portal treatment: desaturated and dimmed, lifting on
 * the parent link's hover.
 */
export function LogoWell({
  icon,
  size = "md",
  muted = false,
}: {
  icon: PortalIcon;
  size?: "sm" | "md";
  muted?: boolean;
}): JSX.Element {
  const theme = useThemeStore((s) => s.theme);
  const src = resolveIconSrc(icon, resolveTheme(theme));
  return (
    <div
      data-testid="logo-well"
      className={cn(
        "flex w-full items-center justify-center bg-panel-well",
        size === "md" ? "px-6 py-5" : "h-14 rounded-lg px-3",
        muted && "opacity-50 saturate-0 transition-all group-hover:opacity-70"
      )}
    >
      <img
        src={src}
        alt={icon.alt}
        className={cn("w-full object-contain", size === "md" ? "h-[60px]" : "h-full max-h-10")}
        loading="lazy"
      />
    </div>
  );
}
